/**
 * Creative Component - loads overlay component. 
 * This view can and should work by itself, but can be embedded by other views to render the overlay
 * No required props, width and height can be passed to override global stores: 
 */

import { useState, useEffect, useCallback } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import Overlay from 'components/atoms/Overlay/Overlay'
import { CreativeType, DisplayBannerT } from "types/creative";
import { useAppState, useAppDispatch } from "context";
import { setCreativeViewSize } from 'context/actions';
import {VIDEO_CONTROLS_HEIGHT} from 'services/constants'
import "./CreativeComponent.scss";


type CreativeComponentProps = {
  videoControls?: boolean, // video controls padding. Used when video controls are bellow creative
  width?: number | string,
  height?: number | string,
  className?: string,
}

const parseValue = (v, extra = 0) => {
  const newValue = !isNaN(Number(v)) ? Number(v) + extra : v;
  return newValue;
}


const CreativeComponent = (props: CreativeComponentProps) => {

  const dispatch = useAppDispatch();

  // for debug only
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const debug = !!(urlParams.get('debug'))

  const {
    creative = {} as CreativeType,
    superFrameLoaded,
    error,
    displayBanner = {} as DisplayBannerT,
    activeCreativeView,
  } = useAppState();

  const { videoControls = false, width: overrideWidth, height: overrideHeight, className } = props; // let outside logic determine if adding space
  const extraSpace = videoControls ? VIDEO_CONTROLS_HEIGHT : 0;
  const { width: dynamicWidth, height: dynamicHeight, fullscreen } = activeCreativeView;

  // use prop size if exists or fall back to global size
  const width = overrideWidth || dynamicWidth;
  const height = overrideHeight || dynamicHeight;


  const [loadingMsg, setLoadingMsg] = useState('Loading Creative...');
  const creativeFullScreen = useFullScreenHandle();
  const [sizeStyle, setSizeStyle] = useState<{ width: number | string, height: number | string }>({ width, height });
  // this should run only when the size or video controls change. 
  useEffect(() => {
    setSizeStyle({ width, height: parseValue(height, extraSpace) });
  }, [width, height, videoControls, displayBanner.active]);

  // handle fullscreen request
  useEffect(() => {
    if (fullscreen) {
      creativeFullScreen.enter();
    } else {
      setSizeStyle({ width, height: parseValue(height, extraSpace) });
    }
  }, [fullscreen])

  // handle fullscreen event
  const onFullScreenChange = useCallback((isOn) => {
    if (isOn) {
      // only changes the css here, we do not store 100% on the global store
      setSizeStyle({ width: '100%', height: '100%' });
    } else {
      // global store already has previous sizes, so we only record that we are no longer in fullscreen
      dispatch(setCreativeViewSize({ fullscreen: false }))
    }
  }, [creativeFullScreen])

  useEffect(() => {
    if (error) {
      setLoadingMsg('Creative failed to load');
    }
  }, [error])

  // wait for all requirements
  if (!superFrameLoaded || !creative.id) {
    return <div className="loading-creative" style={sizeStyle}>{loadingMsg}</div>
  }

  return (
    <FullScreen
      handle={creativeFullScreen}
      onChange={onFullScreenChange}
      className={`${className ? className : ''} ${videoControls ? 'video-controls' : ''}`}
    >
      <div className={`ad-slot ${debug ? 'debug' : ''} ${displayBanner.active ? 'banner' : ''}`} style={sizeStyle} >
        {!debug && <Overlay payload={creative} banner={displayBanner} />}
      </div>
    </FullScreen>
  );
};

export default CreativeComponent;
